<template>
  <div class="faq-section">
    <div class="faq-container">
      <div class="faq-header">
        <h2 class="faq-title">
          {{ t('faq.heading') }}
        </h2>
        <p class="faq-subtitle">
          {{ t('faq.subheading') }}
        </p>
      </div>

      <div class="faq-cards">
        <div class="faq-card">
          <div class="question-icon">
            <span>❓</span>
          </div>
          <h3 class="question-title">{{ t('faq.q1') }}</h3>
          <p class="question-answer">
            {{ t('faq.a1') }}
          </p>
        </div>

        <div class="faq-card">
          <div class="question-icon">
            <span>❓</span>
          </div>
          <h3 class="question-title">{{ t('faq.q2') }}</h3>
          <p class="question-answer">
            {{ t('faq.a2') }}
          </p>
        </div>

        <div class="faq-card">
          <div class="question-icon">
            <span>❓</span>
          </div>
          <h3 class="question-title">{{ t('faq.q3') }}</h3>
          <p class="question-answer">
            {{ t('faq.a3') }}
          </p>
        </div>
      </div>

      <CtaButton @click="$emit('ctaClick')" />
    </div>
  </div>
</template>

<script setup>
import { useLanguage } from '@/composables/useLanguage'
import CtaButton from '@/components/ui/CtaButton.vue';

const { t } = useLanguage()

defineEmits(['ctaClick']);
</script>

<style scoped>
.faq-section {
  width: 100%;
  max-width: 100vw;
  background-color: #fff;
  padding: clamp(40px, 7vw, 70px) clamp(20px, 4vw, 40px);
  box-sizing: border-box;
}

.faq-container {
  max-width: 1760px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(30px, 4vw, 40px);
  box-sizing: border-box;
}

.faq-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(16px, 3vw, 24px);
  text-align: center;
  padding: 0 clamp(10px, 2vw, 20px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.faq-title {
  font-size: clamp(22px, 4.5vw, 40px);
  font-weight: bold;
  line-height: 110%;
  text-transform: uppercase;
  color: #313131;
  opacity: 0.9;
  max-width: 1716px;
  width: 100%;
  margin: 0;
  font-family: Poppins, sans-serif;
}

.faq-subtitle {
  font-size: clamp(15px, 2.8vw, 24px);
  line-height: 130%;
  color: #3a3a3a;
  opacity: 0.9;
  max-width: 1127px;
  width: 100%;
  margin: 0;
  font-family: Poppins, sans-serif;
}

.faq-cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(16px, 2.5vw, 20px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.faq-card {
  flex: 1;
  min-width: min(280px, 100%);
  max-width: 474px;
  width: 100%;
  padding: clamp(24px, 4.5vw, 40px) clamp(20px, 3.5vw, 32px);
  box-sizing: border-box;
  background-color: rgba(242, 242, 242, 0.21);
  border: 0.8px solid #dedede;
  border-radius: clamp(12px, 2vw, 16.6px);
  box-shadow: 0px 3.32px 17.43px rgba(0, 0, 0, 0.07);
  backdrop-filter: blur(14.11px);
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.5vw, 12px);
  align-items: flex-start;
}

.question-icon {
  width: clamp(40px, 6vw, 50.6px);
  height: clamp(40px, 6vw, 50.6px);
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(244, 33, 33, 0.3), rgba(244, 125, 33, 0));
  border: 1px solid #e00000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(20px, 3vw, 25px);
  flex-shrink: 0;
}

.question-title {
  font-size: clamp(18px, 2.5vw, 25px);
  font-weight: bold;
  line-height: 1.3;
  color: #313131;
  margin: 0;
  text-align: left;
  font-family: inherit;
}

.question-answer {
  font-size: clamp(13px, 1.6vw, 16px);
  line-height: 150%;
  color: #3a3a3a;
  margin: 0;
  text-align: left;
  font-family: Poppins, sans-serif;
}

@media (max-width: 1024px) {
  .faq-cards {
    gap: clamp(12px, 2vw, 16px);
  }

  .faq-card {
    min-width: min(280px, 100%);
  }
}

@media (max-width: 768px) {
  .faq-section {
    padding: clamp(35px, 6vw, 50px) clamp(15px, 3vw, 20px);
  }

  .faq-cards {
    flex-direction: column;
    width: 100%;
    gap: clamp(14px, 2.2vw, 18px);
  }

  .faq-card {
    max-width: 100%;
  }
}

@media (max-width: 640px) {
  .faq-section {
    padding: clamp(30px, 5.5vw, 45px) clamp(12px, 2.5vw, 18px);
  }

  .faq-header {
    gap: clamp(12px, 2.5vw, 18px);
  }

  .question-title {
    font-size: clamp(16px, 2.8vw, 20px);
  }

  .question-answer {
    font-size: clamp(12px, 2.2vw, 13px);
  }
}

@media (max-width: 480px) {
  .faq-section {
    padding: clamp(25px, 4.5vw, 40px) clamp(10px, 2vw, 15px);
  }

  .faq-card {
    padding: clamp(20px, 4vw, 25px) clamp(16px, 3vw, 20px);
  }

  .question-icon {
    width: clamp(34px, 7vw, 36px);
    height: clamp(34px, 7vw, 36px);
    font-size: clamp(17px, 3.5vw, 18px);
  }

  .question-title {
    font-size: clamp(15px, 3vw, 16px);
  }

  .question-answer {
    font-size: clamp(11px, 2.2vw, 12px);
  }
}

@media (max-width: 380px) {
  .faq-section {
    padding: 20px 10px;
  }

  .faq-card {
    padding: 18px 14px;
    min-width: min(260px, 100%);
  }

  .question-icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .question-title {
    font-size: 14px;
  }

  .question-answer {
    font-size: 11px;
  }
}
</style>
